import {Rule} from '../../models/svg-rule/rule.model';
import {IRuleSystem} from '../../models/svg-rule/rule-system.model';
import {Color} from '../../enums/color';
import {TcddTrainTabUtil, ValueIndex} from '../../utils/tcdd-train-tab-util';
import {TcddMainSystem} from '../../enums/tcdd-main-system.enum';
import {TcddSubSystem} from '../../enums/tcdd-sub-system.enum';
import {TcddSubSystemType} from '../../enums/tcdd-sub-system-type.enum';
import {MainSystemKeys} from '../../constants/tcdd-system-keys/main-system-keys';
import {CommunicationSystemKeys} from '../../constants/tcdd-system-keys/communication-system-keys';
import {COMMUNICATION_SYSTEM} from '../../constants/tcdd-system-contants/communication-system.constant';
import {AIR_CONDITION_SYSTEM} from '../../constants/tcdd-system-contants/air-condition-system.constant';
import {DOOR_SYSTEM} from '../../constants/tcdd-system-contants/door-system.constant';
import {OTHER_SYSTEM} from '../../constants/tcdd-system-contants/other-system.constant';
import {TCDD_UNIT} from '../../constants/tcdd-unit-constant';
import {createColorRule} from '../../utils/tcdd-rule-util';

function getIndex(key: string): ValueIndex {
  return TcddTrainTabUtil.getValueIndex(
    TcddMainSystem.DIAGNOSTIC_SYSTEM,
    TcddSubSystem.COMMUNICATION_SYSTEM,
    TcddSubSystemType.SUB,
    key
  );
}

export class CommunicationSystemRules implements IRuleSystem {
  public getRules(): Rule[] {
    return [
      createColorRule(TCDD_UNIT.UDS_UIB, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const hasError = data?.[TcddSubSystem.MAIN_SYSTEM]?.[MainSystemKeys.udsUibError] ?? false;
        if (hasError) {
          setColor(Color.Red);
        }
        return hasError;
      }),
      createColorRule(TCDD_UNIT.EVENT_RECORDER, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const eventRecorder: ValueIndex = getIndex(CommunicationSystemKeys.eventRecorder); // === 0 will be pink
        const hasError = Number(wagonData[eventRecorder.groupIndex]?.[eventRecorder.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.EVENT_RECORDER_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.BCU, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const bcu: ValueIndex = getIndex(CommunicationSystemKeys.bcu); // === 0 will be pink
        const hasError = Number(wagonData[bcu.groupIndex]?.[bcu.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.BCU_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.TCU, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const tcu: ValueIndex = getIndex(CommunicationSystemKeys.tcu); // === 0 will be pink
        const hasError = Number(wagonData[tcu.groupIndex]?.[tcu.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.TCU_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.WTB_MVB, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const wtbMvb: ValueIndex = getIndex(CommunicationSystemKeys.wtbMvb); // === 0 will be pink
        const hasError = Number(wagonData[wtbMvb.groupIndex]?.[wtbMvb.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.WTB_MVB_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.CCU, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const ccu: ValueIndex = getIndex(CommunicationSystemKeys.ccu); // === 0 will be pink
        const hasError = Number(wagonData[ccu.groupIndex]?.[ccu.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.CCU_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.HMI_OK, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const hmi: ValueIndex = getIndex(CommunicationSystemKeys.hmi); // === 0 will be pink
        const hasError = Number(wagonData[hmi.groupIndex]?.[hmi.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.HMI_NOK;

        if (hasError) {
          setColor(Color.Transparent);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.HMI_NOK, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const hmi: ValueIndex = getIndex(CommunicationSystemKeys.hmi); // === 0 will be pink
        const noError = Number(wagonData[hmi.groupIndex]?.[hmi.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.HMI_OK;

        if (noError) {
          setColor(Color.Transparent);
        }

        return noError;
      }),
      createColorRule(TCDD_UNIT.ACTIVE_CCU, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const unitRole: ValueIndex = getIndex(CommunicationSystemKeys.unitRole); // === 1 will be active
        const inactive = Number(wagonData[unitRole.groupIndex]?.[unitRole.infoIndex]) !== COMMUNICATION_SYSTEM.STATUS.ACTIVE_CCU;

        if (inactive) {
          setColor(Color.Transparent);
        }

        return inactive;
      }),
      createColorRule(TCDD_UNIT.MVB_EC_POINT, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const mvbRiom1Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom1Error); // === 3 will be pink
        const mvbRiom2Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom2Error); // === 3 will be pink
        const hasError =
          Number(wagonData[mvbRiom1Error.groupIndex]?.[mvbRiom1Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_1_ERROR ||
          Number(wagonData[mvbRiom2Error.groupIndex]?.[mvbRiom2Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_2_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.MVB_CD_POINT, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const mvbRiom3Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom3Error); // === 3 will be pink
        const mvbRiom4Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom4Error); // === 3 will be pink
        const hasError =
          Number(wagonData[mvbRiom3Error.groupIndex]?.[mvbRiom3Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_3_ERROR ||
          Number(wagonData[mvbRiom4Error.groupIndex]?.[mvbRiom4Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_4_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.MVB_RIOM_1, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const mvbRiom1Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom1Error); // === 3 will be pink
        const hasError = Number(wagonData[mvbRiom1Error.groupIndex]?.[mvbRiom1Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_1_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.MVB_RIOM_2, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const mvbRiom2Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom2Error); // === 3 will be pink
        const hasError = Number(wagonData[mvbRiom2Error.groupIndex]?.[mvbRiom2Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_2_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.MVB_RIOM_3, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const mvbRiom3Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom3Error); // === 3 will be pink
        const hasError = Number(wagonData[mvbRiom3Error.groupIndex]?.[mvbRiom3Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_3_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.MVB_RIOM_4, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const mvbRiom4Error: ValueIndex = getIndex(CommunicationSystemKeys.mvbRiom4Error); // === 3 will be pink
        const hasError = Number(wagonData[mvbRiom4Error.groupIndex]?.[mvbRiom4Error.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.MVB_RIOM_4_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.CAN_RIOM, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const canRiomError: ValueIndex = getIndex(CommunicationSystemKeys.canRiomError); // === 1 will be pink
        const hasError = Number(wagonData[canRiomError.groupIndex]?.[canRiomError.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.CAN_RIOM_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.PAPIS, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const papisError: ValueIndex = getIndex(CommunicationSystemKeys.papisError); // === 1 will be pink
        const hasError = Number(wagonData[papisError.groupIndex]?.[papisError.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.PAPIS_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.CCTV, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const cctvError: ValueIndex = getIndex(CommunicationSystemKeys.cctvError); // === 1 will be pink
        const hasError = Number(wagonData[cctvError.groupIndex]?.[cctvError.infoIndex]) === COMMUNICATION_SYSTEM.STATUS.CCTV_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.DRIVER_HVAC, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const driverHvacCommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.driverHvacCommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[driverHvacCommunicationError.groupIndex]?.[driverHvacCommunicationError.infoIndex]) === AIR_CONDITION_SYSTEM.STATUS.DRIVER_HVAC_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.HVAC_1, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const hvac1CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.hvac1CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[hvac1CommunicationError.groupIndex]?.[hvac1CommunicationError.infoIndex]) === AIR_CONDITION_SYSTEM.STATUS.HVAC_1_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.HVAC_2, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const hvac2CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.hvac2CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[hvac2CommunicationError.groupIndex]?.[hvac2CommunicationError.infoIndex]) === AIR_CONDITION_SYSTEM.STATUS.HVAC_2_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.OUTER_DOOR_1, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isOuterDoor1CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isOuterDoor1CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isOuterDoor1CommunicationError.groupIndex]?.[isOuterDoor1CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.OUTER_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.OUTER_DOOR_2, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isOuterDoor2CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isOuterDoor2CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isOuterDoor2CommunicationError.groupIndex]?.[isOuterDoor2CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.OUTER_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.OUTER_DOOR_3, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isOuterDoor3CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isOuterDoor3CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isOuterDoor3CommunicationError.groupIndex]?.[isOuterDoor3CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.OUTER_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.OUTER_DOOR_4, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isOuterDoor4CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isOuterDoor4CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isOuterDoor4CommunicationError.groupIndex]?.[isOuterDoor4CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.OUTER_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.END_DOOR_1, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isEndDoor1CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isEndDoor1CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isEndDoor1CommunicationError.groupIndex]?.[isEndDoor1CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.END_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.END_DOOR_2, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isEndDoor2CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isEndDoor2CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isEndDoor2CommunicationError.groupIndex]?.[isEndDoor2CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.END_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.INNER_DOOR_1, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isInnerDoor1CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isInnerDoor1CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isInnerDoor1CommunicationError.groupIndex]?.[isInnerDoor1CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.INNER_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.INNER_DOOR_2, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isInnerDoor2CommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isInnerDoor2CommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isInnerDoor2CommunicationError.groupIndex]?.[isInnerDoor2CommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.INNER_DOOR_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.BISTRO_DOOR, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const isBistroDoorCommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.isBistroDoorCommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[isBistroDoorCommunicationError.groupIndex]?.[isBistroDoorCommunicationError.infoIndex]) === DOOR_SYSTEM.STATUS.BISTRO_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      }),
      createColorRule(TCDD_UNIT.TOILET, (wagonIndex: number, data: any[], setColor: (color: Color) => void) => {
        const wagonData = data?.[TcddSubSystem.COMMUNICATION_SYSTEM]?.[wagonIndex];
        if (!wagonData) return false;

        const toiletCommunicationError: ValueIndex = getIndex(CommunicationSystemKeys.toiletCommunicationError); // === 1 will be pink
        const hasError = Number(wagonData[toiletCommunicationError.groupIndex]?.[toiletCommunicationError.infoIndex]) === OTHER_SYSTEM.STATUS.TOILET_COMMUNICATION_ERROR;

        if (hasError) {
          setColor(Color.Pink);
        }

        return hasError;
      })
    ];
  }
}
